/**
 * @license
 * Copyright 2026 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 *
 * g3-prettier-ignore-file
 */

/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { GoogleGenAiDefaultError } from "../models/errors/google-gen-ai-default-error.js";
import { Result } from "../types/fp.js";
import { wrapEventStreamResponse } from "./event-streams.js";
import { matchResponse, matchStatusCode, StatusCodePredicate } from "./http.js";
import { isPlainObject } from "./primitives.js";

export type Encoding =
  | "jsonl"
  | "json"
  | "text"
  | "bytes"
  | "stream"
  | "sse"
  | "nil"
  | "fail";

const DEFAULT_CONTENT_TYPES: Record<Encoding, string> = {
  jsonl: "application/jsonl",
  json: "application/json",
  text: "text/plain",
  bytes: "application/octet-stream",
  stream: "application/octet-stream",
  sse: "text/event-stream",
  nil: "*",
  fail: "*",
};

export type SseOptions = {
  sentinel?: string;
  flattened?: boolean;
  dataRequired?: boolean;
};
export type ErrorClassType = new(
  data: any,
  httpMeta: { request: Request; response: Response; body: string },
) => unknown;

type MatchOptions = {
  ctype?: string;
  hdrs?: boolean;
  key?: string;
  sseSentinel?: string;
};

export type ValueMatcher<V> = MatchOptions & {
  enc: Encoding;
  codes: StatusCodePredicate;
  __value?: V;
  sse?: SseOptions;
};

export type ErrorMatcher<E> = MatchOptions & {
  enc: Encoding;
  codes: StatusCodePredicate;
  __error?: E;
  errorClass: ErrorClassType | undefined;
  sse?: SseOptions;
  err: true;
};

export type FailMatcher = {
  enc: "fail";
  codes: StatusCodePredicate;
};

export type Matcher<T, E> = ValueMatcher<T> | ErrorMatcher<E> | FailMatcher;

export function jsonErr<E>(
  codes: StatusCodePredicate,
  errorClass: ErrorClassType | undefined,
  options?: MatchOptions,
): ErrorMatcher<E> {
  return { ...options, err: true, enc: "json", codes, errorClass };
}
export function json<T>(
  codes: StatusCodePredicate,
  options?: MatchOptions,
): ValueMatcher<T> {
  return { ...options, enc: "json", codes };
}

export function jsonl<T>(
  codes: StatusCodePredicate,
  options?: MatchOptions,
): ValueMatcher<T> {
  return { ...options, enc: "jsonl", codes };
}

export function jsonlErr<E>(
  codes: StatusCodePredicate,
  errorClass: ErrorClassType | undefined,
  options?: MatchOptions,
): ErrorMatcher<E> {
  return { ...options, err: true, enc: "jsonl", codes, errorClass };
}

export function textErr<E>(
  codes: StatusCodePredicate,
  errorClass: ErrorClassType | undefined,
  options?: MatchOptions,
): ErrorMatcher<E> {
  return { ...options, err: true, enc: "text", codes, errorClass };
}
export function text<T>(
  codes: StatusCodePredicate,
  options?: MatchOptions,
): ValueMatcher<T> {
  return { ...options, enc: "text", codes };
}

export function bytesErr<E>(
  codes: StatusCodePredicate,
  errorClass: ErrorClassType | undefined,
  options?: MatchOptions,
): ErrorMatcher<E> {
  return { ...options, err: true, enc: "bytes", codes, errorClass };
}
export function bytes<T>(
  codes: StatusCodePredicate,
  options?: MatchOptions,
): ValueMatcher<T> {
  return { ...options, enc: "bytes", codes };
}

export function streamErr<E>(
  codes: StatusCodePredicate,
  errorClass: ErrorClassType | undefined,
  options?: MatchOptions,
): ErrorMatcher<E> {
  return { ...options, err: true, enc: "stream", codes, errorClass };
}
export function stream<T>(
  codes: StatusCodePredicate,
  options?: MatchOptions,
): ValueMatcher<T> {
  return { ...options, enc: "stream", codes };
}

export function sseErr<E>(
  codes: StatusCodePredicate,
  errorClass: ErrorClassType | undefined,
  sse?: SseOptions,
  options?: MatchOptions,
): ErrorMatcher<E> {
  return {
    ...options,
    err: true,
    enc: "sse",
    codes,
    errorClass,
    ...(sse ? { sse } : {}),
  };
}
export function sse<T>(
  codes: StatusCodePredicate,
  sse?: SseOptions,
  options?: MatchOptions,
): ValueMatcher<T> {
  return { ...options, enc: "sse", codes, ...(sse ? { sse } : {}) };
}

export function nilErr<E>(
  codes: StatusCodePredicate,
  errorClass: ErrorClassType | undefined,
  options?: MatchOptions,
): ErrorMatcher<E> {
  return { ...options, err: true, enc: "nil", codes, errorClass };
}
export function nil<T>(
  codes: StatusCodePredicate,
  options?: MatchOptions,
): ValueMatcher<T> {
  return { ...options, enc: "nil", codes };
}

export function fail(codes: StatusCodePredicate): FailMatcher {
  return { enc: "fail", codes };
}

export type MatchedValue<Matchers> = Matchers extends Matcher<infer T, any>[]
  ? T
  : never;
export type MatchedError<Matchers> = Matchers extends Matcher<any, infer E>[]
  ? E
  : never;
export type MatchFunc<T, E> = (
  response: Response,
  request: Request,
  options?: { resultKey?: string; extraFields?: Record<string, unknown> },
) => Promise<[result: Result<T, E>, raw: unknown]>;

export function match<T, E>(
  ...matchers: Array<Matcher<T, E>>
): MatchFunc<T, E | GoogleGenAiDefaultError> {
  return async function matchFunc(
    response: Response,
    request: Request,
    options?: { resultKey?: string; extraFields?: Record<string, unknown> },
  ): Promise<[result: Result<T, E | GoogleGenAiDefaultError>, raw: unknown]> {
    let raw: unknown;
    let matcher: Matcher<T, E> | undefined;
    for (const match of matchers) {
      const { codes } = match;
      const ctpattern = "ctype" in match
        ? match.ctype
        : DEFAULT_CONTENT_TYPES[match.enc];
      if (ctpattern && matchResponse(response, codes, ctpattern)) {
        matcher = match;
        break;
      } else if (!ctpattern && matchStatusCode(response, codes)) {
        matcher = match;
        break;
      }
    }

    if (!matcher) {
      return [{
        ok: false,
        error: new GoogleGenAiDefaultError(
          "Unexpected Status or Content-Type",
          {
            response,
            request,
            body: await response.text().catch(() => ""),
          },
        ),
      }, raw];
    }

    const encoding = matcher.enc;
    let body = "";
    switch (encoding) {
      case "json":
        body = await response.text();
        try {
          raw = JSON.parse(body);
        } catch (err) {
          // Passthrough for malformed error bodies; success bodies must be valid.
          if (!("err" in matcher)) {
            throw err;
          }
          raw = body;
        }
        break;
      case "jsonl":
        raw = response.body;
        break;
      case "bytes":
        raw = new Uint8Array(await response.arrayBuffer());
        break;
      case "stream":
        raw = response.body;
        break;
      case "text":
        body = await response.text();
        raw = body;
        break;
      case "sse":
        if (response.body) {
          const sseOpts = ("sse" in matcher && matcher.sse) || {};
          raw = wrapEventStreamResponse(response.body, sseOpts);
        } else {
          raw = null;
        }
        break;
      case "nil":
        body = await response.text();
        raw = undefined;
        break;
      case "fail":
        body = await response.text();
        raw = body;
        break;
      default:
        throw new Error(
          `Unsupported response type: ${encoding satisfies never}`,
        );
    }

    if (matcher.enc === "fail") {
      return [{
        ok: false,
        error: new GoogleGenAiDefaultError("API error occurred", {
          request,
          response,
          body,
        }),
      }, raw];
    }

    const resultKey = matcher.key || options?.resultKey;
    let data: unknown;
    const headersField = matcher.hdrs
      ? { headers: unpackHeaders(response.headers) }
      : null;

    if ("err" in matcher) {
      data = {
        ...options?.extraFields,
        ...headersField,
        ...(isPlainObject(raw) ? raw : null),
      };
    } else if (resultKey) {
      data = {
        ...options?.extraFields,
        ...headersField,
        [resultKey]: raw,
      };
    } else if (matcher.hdrs) {
      data = {
        ...options?.extraFields,
        ...headersField,
        ...(isPlainObject(raw) ? raw : null),
      };
    } else {
      data = raw;
    }

    if ("err" in matcher) {
      // When the spec error response is a single error class, instantiate
      // it with the data + httpMeta. When the spec defines a discriminated
      // union of error variants (no class), `errorClass` is undefined and
      // we pass the parsed payload through as the error value directly.
      const errValue = matcher.errorClass
        ? new matcher.errorClass(data, { request, response, body }) as E
        : data as E;
      return [{ ok: false, error: errValue }, raw];
    }
    return [{ ok: true, value: data as T }, raw];
  };
}

const headerValRE = /, */;
/**
 * Iterates over a Headers object and returns an object with all the header
 * entries. Values are represented as an array to account for repeated headers.
 */
export function unpackHeaders(headers: Headers): Record<string, string[]> {
  const out: Record<string, string[]> = {};

  for (const [k, v] of headers.entries()) {
    out[k] = v.split(headerValRE);
  }

  return out;
}
